import logger from '../utils/logger-rum';
import { showAlert } from '../components/general-components/alert-service';
import { HttpRequest } from '../stores/types/types';
import { buildSyncUrl, getSyncHeaders } from './utils';

interface ScheduledStreamBase {
	id: number;
    createdByUser: number;
    updatedByUser: number;
    meetingId: number;
	status: ScheduledStreamStatus;
}

// Handle incoming UTC date strings from the API
export interface ScheduledStreamResponse extends ScheduledStreamBase {
    startTime: string;
    createdDate: string;
    updatedDate: string;
}

export enum ScheduledStreamStatus {
	None = 'none',
	Scheduled = 'scheduled',
    Prewarm = 'prewarm',
    Starting = 'starting',
	Started = 'started',
	Ended = 'ended',
}

// Convert to local timezone JS Date objects
export interface ScheduledStream extends ScheduledStreamBase {
    startTime: Date;
    createdDate: Date;
    updatedDate: Date;
}

export const scheduledStreamActiveStates = [
	ScheduledStreamStatus.Starting,
	ScheduledStreamStatus.Started,
];

export interface ScheduledStreamValidation {
    shotId: number;
    isValid: boolean;
    message: string;
}

const baseRoute = 'v1/scheduled-streams';

// Type predicate for ScheduledStreamResponse
export const isStreamResponse = (
	res: ScheduledStreamResponse | {},
): res is ScheduledStreamResponse => (
	(res as ScheduledStreamResponse).id !== undefined
);

export const CreateScheduledStream = async (
	token: string,
	meetingId: number,
	userId: number,
	startTime: Date,
): Promise<ScheduledStreamResponse | null> => {
	try {
		const minutes = 3;
		const now = new Date(new Date().getTime() + (minutes * 60000));

		if (startTime < now || !startTime) {
			showAlert({
				message: 'Cannot schedule stream',
				description: 'Must be 3+ minutes in the future',
				type: 'scheduled-stream-schedule-error',
				duration: 5000,
			});
			logger.error(
				'CreateScheduledStream error: Invalid start time.',
			);
			return null;
		}

		const response = await fetch(
			buildSyncUrl(`${baseRoute}/${meetingId}/${userId}`),
			{
				method: HttpRequest.Post,
				headers: getSyncHeaders(token),
				body: JSON.stringify({
					startTime,
					status: ScheduledStreamStatus.Scheduled,
				}),
			},
		);

		if (response.ok) {
			const result: ScheduledStreamResponse | {} = await response.json();
			if (result && isStreamResponse(result)) {
				return result;
			}
		}

		if (response.status === 400 || response.status === 500) {
			showAlert({
				message: 'Cannot schedule stream',
				description: 'Must be 3+ minutes in the future',
				type: 'scheduled-stream-schedule-error',
				duration: 5000,
			});
		}

		logger.error(
			'CreateScheduledStream error: Unable to create scheduled stream.',
		);
	} catch (error) {
		logger.error(`CreateScheduledStream error: ${error}`);
	}

	return null;
};

export const GetScheduledStream = async (
	token: string,
	meetingId: number,
): Promise<ScheduledStreamResponse | null> => {
	try {
		const response = await fetch(buildSyncUrl(`${baseRoute}/${meetingId}`), {
			method: HttpRequest.Get,
			headers: getSyncHeaders(token),
		});

		if (response.ok) {
			const result: ScheduledStreamResponse | {} = await response.json();
			if (result && isStreamResponse(result)) {
				return result;
			}
		}

		logger.error(
			'GetScheduledStreams error: Unable to get scheduled streams.',
		);
	} catch (error) {
		logger.error(`GetScheduledStreams error: ${error}`);
	}

	return null;
};

export const UpdateScheduledStream = async (
	token: string,
	meetingId: number,
	scheduledStreamId: number,
	userId: number,
	startTime: Date,
	status: ScheduledStreamStatus,
): Promise<ScheduledStreamResponse | null> => {
	try {
		const minutes = 3;
		const now = new Date(new Date().getTime() + (minutes * 60000));

		if (startTime < now || !startTime) {
			showAlert({
				message: 'Cannot schedule stream',
				description: 'Must be 3+ minutes in the future',
				type: 'scheduled-stream-schedule-error',
				duration: 5000,
			});
			logger.error(
				'CreateScheduledStream error: Invalid start time.',
			);
			return null;
		}
		const response = await fetch(
			buildSyncUrl(`${baseRoute}/${meetingId}/${scheduledStreamId}/${userId}`),
			{
				method: HttpRequest.Put,
				headers: getSyncHeaders(token),
				body: JSON.stringify({
					startTime,
					status,
				}),
			},
		);

		if (response.ok) {
			const result: ScheduledStreamResponse | {} = await response.json();
			if (result && isStreamResponse(result)) {
				return result;
			}
		}
		if (response.status === 400 || response.status === 500) {
			showAlert({
				message: 'Cannot schedule stream',
				description: 'Must be 3+ minutes in the future',
				type: 'scheduled-stream-schedule-error',
				duration: 5000,
			});
		}
		logger.error(
			'UpdateScheduledStream error: Unable to update scheduled stream.',
		);
	} catch (error) {
		logger.error(`UpdateScheduledStream error: ${error}`);
	}

	return null;
};

export const UpdateScheduledStreamStatus = async (
	token: string,
	meetingId: number,
	scheduledStreamId: number,
	userId: number,
	status: ScheduledStreamStatus,
): Promise<ScheduledStreamResponse | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`${baseRoute}/${meetingId}/${scheduledStreamId}/${userId}/${status}`),
			{
				method: HttpRequest.Put,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			const result: ScheduledStreamResponse | {} = await response.json();

			if (result && isStreamResponse(result)) {
				return result;
			}
		}

		logger.error(
			'UpdateScheduledStreamStatus error: Unable to update scheduled stream status.',
		);
	} catch (error) {
		logger.error(`UpdateScheduledStreamStatus error: ${error}`);
	}

	return null;
};

export const DeleteScheduledStream = async (
	token: string,
	meetingId: number,
	scheduledStreamId: number,
	userId: number,
): Promise<boolean> => {
	try {
		const response = await fetch(
			buildSyncUrl(`${baseRoute}/${meetingId}/${scheduledStreamId}/${userId}`),
			{
				method: HttpRequest.Delete,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			const outcome: boolean = await response.json();

			if (outcome) {
				return true;
			}
		}

		logger.error(
			'DeleteScheduledStream error: Unable to delete scheduled stream.',
		);
	} catch (error) {
		logger.error(`DeleteScheduledStream error: ${error}`);
	}

	return false;
};

export const GetScheduledStreamValidation = async (
	token: string,
	meetingId: number,
): Promise<ScheduledStreamValidation[]> => {
	try {
		const response = await fetch(
			buildSyncUrl(`${baseRoute}-validation/${meetingId}`),
			{
				method: HttpRequest.Get,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.json();
		}

		logger.error('GetScheduledStreamValidation error: Unable to get validation.');
	} catch (error) {
		logger.error(`GetScheduledStreamValidation error: ${error}`);
	}

	return [];
};
